import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertTriangle,
  PlusCircle,
  Download,
  Calendar,
  User,
  Search,
  Filter,
  FileCheck,
  ShieldAlert,
  ClipboardList,
} from "lucide-react";

const TIPOS_LLAMADA = ["Verbal", "Escrita", "Falta Grave", "Suspensión"];

const MOTIVOS = [
  "Puntualidad / Retardos",
  "Inasistencia Injustificada",
  "Bajo Desempeño Laboral",
  "Falta de Disciplina / Conducta",
  "Incumplimiento de Políticas / Procedimientos",
  "Uso Inadecuado de Herramientas de la Empresa",
  "Confidencialidad y Seguridad",
  "Otro",
];

const TipoBadge = ({ tipo }) => {
  const configs = {
    verbal: { label: "Verbal", className: "bg-blue-100 text-blue-800 border-blue-300" },
    escrita: { label: "Escrita", className: "bg-amber-100 text-amber-800 border-amber-300" },
    "falta grave": { label: "Falta Grave", className: "bg-orange-100 text-orange-800 border-orange-300" },
    suspensión: { label: "Suspensión", className: "bg-rose-100 text-rose-800 border-rose-300" },
    suspension: { label: "Suspensión", className: "bg-rose-100 text-rose-800 border-rose-300" },
  };
  const key = tipo?.toLowerCase() || "escrita";
  const config = configs[key] || { label: tipo, className: "bg-slate-100 text-slate-800 border-slate-300" };

  return (
    <Badge variant="outline" className={`font-semibold px-2.5 py-0.5 ${config.className}`}>
      {config.label}
    </Badge>
  );
};

export default function LlamadasAtencionPage() {
  const { user } = useAuth();
  const canCreate = user?.rol === "supervisor" || user?.rol === "administrador" || user?.rol === "rrhh";

  const [llamadas, setLlamadas] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterTipo, setFilterTipo] = useState("todos");

  // Modal Crear
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [formData, setFormData] = useState({
    empleado_id: "",
    tipo_llamada: "Escrita",
    motivo: "Puntualidad / Retardos",
    descripcion: "",
    fecha_incidente: new Date().toISOString().split("T")[0],
    plan_accion: "",
  });
  const [fileAdjunto, setFileAdjunto] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.getLlamadas();
      setLlamadas(res.data || []);

      if (canCreate) {
        // Cargar colaboradores para el selector
        const empRes = await api.getEmployees();
        // Si es supervisor, filtrar solo subordinados (o todos si admin/rrhh)
        let availableEmps = empRes.data || [];
        if (user?.rol === "supervisor") {
          availableEmps = availableEmps.filter((e) => e.supervisor_id === user.id);
        }
        setEmployees(availableEmps);
      }
    } catch (err) {
      toast.error("Error al cargar llamadas de atención");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [user]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.empleado_id) {
      toast.error("Selecciona al colaborador");
      return;
    }
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append("empleado_id", formData.empleado_id);
      form.append("tipo_llamada", formData.tipo_llamada);
      form.append("motivo", formData.motivo);
      form.append("descripcion", formData.descripcion);
      form.append("fecha_incidente", formData.fecha_incidente);
      if (formData.plan_accion) form.append("plan_accion", formData.plan_accion);
      if (fileAdjunto) form.append("archivo", fileAdjunto);

      const res = await api.createLlamada(form);
      const msgEmail = res.data?.tiene_email 
        ? "Notificado por correo con el acta PDF adjunta y en la app." 
        : "Notificado por el sistema y Web Push (sin correo registrado).";
      toast.success(`Llamada de atención registrada. ${msgEmail}`);
      setIsCreateOpen(false);
      setFormData({
        empleado_id: "",
        tipo_llamada: "Escrita",
        motivo: "Puntualidad / Retardos",
        descripcion: "",
        fecha_incidente: new Date().toISOString().split("T")[0],
        plan_accion: "",
      });
      setFileAdjunto(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrar la llamada");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadActaOficial = async (id, nombreEmp = "Colaborador") => {
    try {
      const res = await api.downloadActaPdf(id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Acta_Oficial_${nombreEmp}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Acta oficial descargada en PDF");
    } catch (err) {
      toast.error("Error al descargar el acta en PDF");
    }
  };

  const handleDownload = async (id, nombre = "Soporte_Llamada.pdf") => {
    try {
      const res = await api.downloadLlamadaAdjunto(id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", nombre);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Descarga iniciada");
    } catch (err) {
      toast.error("Error al descargar archivo de respaldo");
    }
  };

  const filteredLlamadas = llamadas.filter((l) => {
    const matchesSearch =
      l.empleado_nombre?.toLowerCase().includes(search.toLowerCase()) ||
      l.motivo?.toLowerCase().includes(search.toLowerCase()) ||
      l.descripcion?.toLowerCase().includes(search.toLowerCase()) ||
      l.supervisor_nombre?.toLowerCase().includes(search.toLowerCase());
    const matchesTipo = filterTipo === "todos" || l.tipo_llamada?.toLowerCase() === filterTipo.toLowerCase();
    return matchesSearch && matchesTipo;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2.5">
            <div className="p-2 bg-amber-100 rounded-lg text-amber-600">
              <AlertTriangle className="w-6 h-6" />
            </div>
            Registro de Llamadas de Atención
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Control y registro formal de retroalimentación disciplinaria a colaboradores
          </p>
        </div>
        {canCreate && (
          <Button
            onClick={() => setIsCreateOpen(true)}
            className="bg-amber-600 hover:bg-amber-700 text-white shadow-sm flex items-center gap-2"
          >
            <PlusCircle className="w-4 h-4" />
            Registrar Llamada de Atención
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500">Total Registros</p>
              <h3 className="text-2xl font-bold text-slate-900 mt-0.5">{llamadas.length}</h3>
            </div>
            <div className="p-2.5 bg-slate-100 text-slate-600 rounded-xl">
              <ClipboardList className="w-5 h-5" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-blue-600 font-medium">Verbales</p>
              <h3 className="text-2xl font-bold text-slate-900 mt-0.5">
                {llamadas.filter((l) => l.tipo_llamada?.toLowerCase().includes("verbal")).length}
              </h3>
            </div>
            <div className="p-2.5 bg-blue-50 text-blue-600 rounded-xl">
              <FileCheck className="w-5 h-5" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-amber-600 font-medium">Escritas</p>
              <h3 className="text-2xl font-bold text-slate-900 mt-0.5">
                {llamadas.filter((l) => l.tipo_llamada?.toLowerCase().includes("escrita")).length}
              </h3>
            </div>
            <div className="p-2.5 bg-amber-50 text-amber-600 rounded-xl">
              <AlertTriangle className="w-5 h-5" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-rose-600 font-medium">Graves / Suspensión</p>
              <h3 className="text-2xl font-bold text-slate-900 mt-0.5">
                {llamadas.filter((l) => l.tipo_llamada?.toLowerCase().includes("grave") || l.tipo_llamada?.toLowerCase().includes("suspens")).length}
              </h3>
            </div>
            <div className="p-2.5 bg-rose-50 text-rose-600 rounded-xl">
              <ShieldAlert className="w-5 h-5" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-center justify-between bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <Input
            placeholder="Buscar colaborador, motivo..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Filter className="w-4 h-4 text-slate-400" />
          <Select value={filterTipo} onValueChange={setFilterTipo}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Tipo de Falta" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos los Tipos</SelectItem>
              <SelectItem value="verbal">Verbal</SelectItem>
              <SelectItem value="escrita">Escrita</SelectItem>
              <SelectItem value="falta grave">Falta Grave</SelectItem>
              <SelectItem value="suspensión">Suspensión</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table / List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 text-slate-600 border-b border-slate-200 font-medium">
              <tr>
                <th className="px-4 py-3">Colaborador</th>
                <th className="px-4 py-3">Tipo y Motivo</th>
                <th className="px-4 py-3">Fecha Incidente</th>
                <th className="px-4 py-3">Descripción y Compromiso</th>
                <th className="px-4 py-3">Registrado Por</th>
                <th className="px-4 py-3 text-right">Soporte</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-10 text-slate-500">
                    Cargando llamadas de atención...
                  </td>
                </tr>
              ) : filteredLlamadas.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-10 text-slate-500">
                    No se encontraron registros de llamadas de atención
                  </td>
                </tr>
              ) : (
                filteredLlamadas.map((l) => (
                  <tr key={l.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-800">{l.empleado_nombre}</div>
                      <div className="text-xs text-slate-400">
                        DPI: {l.empleado_dpi} | {l.puesto}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 mb-1">
                        <TipoBadge tipo={l.tipo_llamada} />
                      </div>
                      <div className="text-xs font-medium text-slate-700">{l.motivo}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                      {l.fecha_incidente}
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      <p className="text-xs text-slate-700 line-clamp-2">{l.descripcion}</p>
                      {l.plan_accion && (
                        <p className="text-xs text-emerald-700 font-medium mt-1 line-clamp-1">
                          Compromiso: {l.plan_accion}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">
                      <div>{l.supervisor_nombre}</div>
                      <div className="text-slate-400">{l.fecha_registro?.split(" ")[0]}</div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownloadActaOficial(l.id, l.empleado_nombre)}
                          className="border-amber-200 text-amber-800 hover:bg-amber-50 text-xs shadow-sm h-7 px-2.5"
                          title="Descargar Acta Administrativa Oficial en PDF"
                        >
                          <Download className="w-3.5 h-3.5 mr-1 text-amber-600" />
                          Acta PDF
                        </Button>

                        {l.tiene_adjunto && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDownload(l.id, `Soporte_${l.empleado_nombre}.pdf`)}
                            className="text-xs text-slate-500 hover:bg-slate-100 h-7 px-2"
                            title="Descargar evidencia adjunta"
                          >
                            Adjunto
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal: Crear Llamada de Atención */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-5 h-5" />
              Registrar Llamada de Atención
            </DialogTitle>
            <DialogDescription>
              El registro quedará archivado formalmente en el expediente del colaborador y se le notificará vía correo electrónico.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreate} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="empleado">Colaborador *</Label>
              <Select
                value={formData.empleado_id}
                onValueChange={(val) => setFormData({ ...formData, empleado_id: val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar colaborador..." />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.nombre} {emp.apellido} ({emp.puesto} - {emp.departamento})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="tipo">Severidad / Tipo *</Label>
                <Select
                  value={formData.tipo_llamada}
                  onValueChange={(val) => setFormData({ ...formData, tipo_llamada: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS_LLAMADA.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="fecha_incidente">Fecha del Incidente *</Label>
                <Input
                  id="fecha_incidente"
                  type="date"
                  value={formData.fecha_incidente}
                  onChange={(e) => setFormData({ ...formData, fecha_incidente: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="motivo">Motivo Principal *</Label>
              <Select
                value={formData.motivo}
                onValueChange={(val) => setFormData({ ...formData, motivo: val })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MOTIVOS.map((m) => (
                    <SelectItem key={m} value={m}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="descripcion">Descripción de los Hechos *</Label>
              <Textarea
                id="descripcion"
                placeholder="Detalla de forma objetiva lo sucedido..."
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                rows={3}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="plan_accion">Compromiso / Plan de Acción Acordado</Label>
              <Textarea
                id="plan_accion"
                placeholder="Compromisos y plazos para corregir la falta..."
                value={formData.plan_accion}
                onChange={(e) => setFormData({ ...formData, plan_accion: e.target.value })}
                rows={2}
              />
            </div>

            <div className="space-y-2 bg-slate-50 p-3 rounded-lg border border-slate-200">
              <Label htmlFor="acta_file" className="text-xs font-semibold text-slate-700 block">
                Adjuntar Acta Firmada o Evidencia (Opcional)
              </Label>
              <Input
                id="acta_file"
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={(e) => setFileAdjunto(e.target.files?.[0] || null)}
                className="bg-white text-xs"
              />
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting} className="bg-amber-600 hover:bg-amber-700 text-white">
                {submitting ? "Guardando..." : "Registrar y Notificar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
